import { Anchor, Truck, Package, MapPin, Navigation, Clock, ArrowRight, CheckCircle2 } from 'lucide-react';

export enum MovementType {
    DF = 'DF', // Discharge full
    FC = 'FC', // Full to consignee
    IE = 'IE', // Import to export
    RE = 'RE', // Return empty
    ES = 'ES', // Empty to shipper
    FL = 'FL', // Full of load
    OF = 'OF', // Ocean full
    OE = 'OE', // Ocean empty
    UN = 'UN'  // Unknown
}

export interface BookingContainer {
    id: string;
    containerNumber: string;
    size: string;
    type: string;
    sealNumber: string;
    grossWeight: number;
    packageNumber: number;
    movements: Movement[];
    // Computed helper for frontend logic
    currentStatus?: MovementType;
}

export interface BookingBill {
    id: string;
    billNumber: string;
    containers: BookingContainer[];
    translatedCommodity?: string|null;
    commodity?: string|null;
}

export interface Movement {
  movement: string;
  date: Date | null;
  vesselName: string | null;
  loadingPort: string | null;
  dischargePort: string | null;
}

export interface ActiveVesselData {
    vessel: YML_Vessel_Schedule;
    containers: Array<{
        container: BookingContainer;
        booking: Booking;
        bill: BookingBill;
        movements: Movement[];
    }>;
}

export interface Route {
    id: string;
    pol: string; // Port of Loading
    pod: string; // Port of Discharge
    vesselName: string;
    vesselCode: string; // yml_route.commonVoyageCode
    etd: Date;
    eta: Date;
}

export interface Booking {
    bookingReference: string;
    clientName: string;
}

// YML Schedule Entity (Mocking the real entity structure)
export interface YML_Vessel_Schedule {
    id: string;
    vesselName: string; // yml_vessel.name
    voyageCode: string; // yml_route.commonVoyageCode
    currentLocation: {
        lat: number;
        lng: number;
        name: string; // Current port or coordinates description
        heading: number; // 0-360 degrees
    };
    nextPort: string;
    etaNextPort: Date;
}

export interface PortData {
    id: string;
    name: string;
    coordinates: { lat: number; lng: number };
}

export interface MapContainerData {
    container: BookingContainer;
    booking: Booking;
    bill: BookingBill;
    movements: Movement[];
}

export interface ActiveEntityData {
    containers: MapContainerData[];
}

export interface PortData {
    id: string;
    name: string;
    coordinates: { lat: number; lng: number };
}

export interface ActiveVesselData extends ActiveEntityData {
    vessel: YML_Vessel_Schedule;
}

export interface ActivePortData extends ActiveEntityData {
    port: PortData;
}

export function getMovementDescription(movementType: MovementType): string {
    switch (movementType) {
        case MovementType.DF:
            return 'Container descărcat plin';
        case MovementType.FC:
            return 'Containerul a plecat plin către destinatar';
        case MovementType.IE:
            return 'Containerul a fost returnat gol';
        case MovementType.RE:
            return 'Containerul a fost returnat gol';
        case MovementType.ES:
            return 'Containerul gol a fost trimis expeditorului';
        case MovementType.FL:
            return 'Container este pregătit pentru încărcare';
        case MovementType.OF:
            return 'Containerul a fost încărcat pe navă';
        case MovementType.OE:
            return 'Containerul gol a fost încărcat pe navă';
        case MovementType.UN:
            return 'Necunoscută';
        default:
            return 'Necunoscută';
    }   
}

export const MOVEMENT_TYPES: Record<string, { label: string; icon: any; color: string }> = {
  'DF': { label: 'Discharge Full', icon: Package, color: 'text-blue-600 bg-blue-100' },
  'DE': { label: 'Discharge Empty', icon: Package, color: 'text-gray-600 bg-gray-100' },
  'FC': { label: 'Full to Consignee', icon: Truck, color: 'text-green-600 bg-green-100' },
  'IE': { label: 'Import to Export', icon: ArrowRight, color: 'text-purple-600 bg-purple-100' },
  'RE': { label: 'Return Empty', icon: ArrowRight, color: 'text-orange-600 bg-orange-100' },
  'ES': { label: 'Empty to Shipper', icon: Truck, color: 'text-yellow-600 bg-yellow-100' },
  'FL': { label: 'Full Load', icon: Package, color: 'text-indigo-600 bg-indigo-100' },
  'OF': { label: 'Ocean Full', icon: Anchor, color: 'text-blue-700 bg-blue-50' },
  'OE': { label: 'Ocean Empty', icon: Anchor, color: 'text-gray-700 bg-gray-50' },
  'UN': { label: 'Unknown', icon: CheckCircle2, color: 'text-gray-400 bg-gray-100' }
};
